/**
 * FAVORIS DE LA BARRE LATÉRALE — RÈGLES PARTAGÉES SERVEUR ET NAVIGATEUR (24/10/2026).
 *
 * Un favori est une ADRESSE de Gestion (chemin + paramètres, jamais d'ancre)
 * et un NOM en texte brut. L'adresse finit dans trois endroits qui suivent un
 * lien sans réfléchir : le `<a href>` natif (Ctrl-clic), le « nouvel onglet »
 * du menu clic droit (`window.open`) et l'interception globale des liens du
 * shell, qui prend tout href commençant par « / ». « //evil.com »,
 * « /\evil.com » ou « /<TAB>/evil.com » y deviendraient une autre origine :
 * c'est la redirection ouverte que ces règles ferment.
 *
 * Elles REFUSENT plutôt que corriger. Une adresse qui ne fait pas l'aller-retour
 * à l'identique par `new URL` (« .. », un caractère que le navigateur
 * réencoderait), ou dont le chemin porte un « % » (« %2e%2e », « %61pi »),
 * est refusée telle quelle : ce qui est stocké est ce qui sera suivi, octet
 * pour octet.
 *
 * Ce module est volontairement SANS AUCUNE dépendance : il se charge dans
 * l'API (par `packages/core`) comme dans le navigateur (sous-chemin
 * `@gestion/shared/favoris`, jamais le barrel, qui exporte le logger). Le
 * CHECK SQL de `user_favorite` n'est qu'un filet ; la règle complète est ici.
 */
/** Au plus 30 favoris par compte (décision d'Hugo, 24/10/2026). */
export const FAVORIS_MAX = 30;
/** Longueur maximale d'une adresse épinglée, en caractères ASCII. */
export const FAVORI_CHEMIN_MAX = 300;
/** Longueur maximale d'un nom de favori, en points de code (comme `char_length`). */
export const FAVORI_LIBELLE_MAX = 80;
/**
 * COPIE de `FULL_LOAD_PREFIXES` (apps/web/src/design/live-control/router.tsx) :
 * les parcours d'authentification et les pages hors shell. Le module du
 * routeur touche `window` dès son chargement, il n'est donc pas importable
 * ici. Le test anti-dérive (tests/favoris-validateur.test.ts) relit la liste
 * du routeur et exige que chacun de ses préfixes soit refusé.
 */
const PREFIXES_HORS_SHELL = ['/login', '/session-expired', '/access-denied', '/design/', '/pointeuse', '/porte/connexion'];
/** Préfixes refusés, sans tenir compte de la casse : API, services, sondes, parcours mobiles. */
const PREFIXES_REFUSES = ['/api/', '/internal/', '/health', '/mobile/auth/', ...PREFIXES_HORS_SHELL];
/** Adresses exactes refusées (chemin seul, sans tenir compte de la casse). */
const CHEMINS_REFUSES = ['/api', '/internal', '/mobile/auth'];
/** La fiche d'un incident, éphémère : « les incidents ne s'épinglent pas »
 *  (décision d'Hugo, 24/10/2026), ni à l'étoile ni au clic droit. La liste
 *  `/infrastructure/incidents`, elle, s'épingle. */
const PREFIXES_INCIDENT = ['/infrastructure/incidents/'];
/** Origine fictive de l'aller-retour : `.invalid` ne résout jamais (RFC 2606). */
const ORIGINE_TEST = 'https://gestion.invalid';
export const RAISON_PAS_GESTION = 'Seule une page de Gestion peut être ajoutée aux favoris.';
export const RAISON_PAGE_REFUSEE = 'Cette page ne peut pas être ajoutée aux favoris.';
export const RAISON_ANCRE = 'Une adresse avec une ancre (#) ne peut pas être ajoutée.';
export const RAISON_TROP_LONGUE = `Cette adresse est trop longue pour un favori (${FAVORI_CHEMIN_MAX} caractères au plus).`;
export const RAISON_LIBELLE = `Nom du favori : 1 à ${FAVORI_LIBELLE_MAX} caractères visibles.`;
export const RAISON_INCIDENT = 'Un incident ne s’ajoute pas aux favoris.';
/** ASCII imprimable, sans espace : ni tabulation, ni contrôle, ni Unicode. */
const ASCII_IMPRIMABLE = /^[\x21-\x7E]+$/;
/**
 * Vérifie une adresse de favori. Rend l'adresse INCHANGÉE si elle est
 * acceptée ; jamais une version « nettoyée ».
 */
export function validerCheminFavori(brut) {
    if (typeof brut !== 'string' || brut.length === 0)
        return { ok: false, raison: RAISON_PAS_GESTION };
    if (brut.length > FAVORI_CHEMIN_MAX)
        return { ok: false, raison: RAISON_TROP_LONGUE };
    if (brut.includes('#'))
        return { ok: false, raison: RAISON_ANCRE };
    if (!ASCII_IMPRIMABLE.test(brut))
        return { ok: false, raison: RAISON_PAS_GESTION };
    // Un chemin de Gestion : « / » d'abord, jamais « // » ni « /\ » (une autre
    // origine pour le navigateur), jamais de « \ » ailleurs.
    if (brut[0] !== '/' || brut[1] === '/' || brut[1] === '\\' || brut.includes('\\')) {
        return { ok: false, raison: RAISON_PAS_GESTION };
    }
    // Jamais de « % » dans le chemin : `new URL` ne décode pas « %61 », mais
    // Fastify, si (« /%61pi/v1/me » atteint /api/v1/me). Les préfixes refusés
    // se contourneraient. Aucune route de Gestion n'en a (uuid, slugs, dates) ;
    // les paramètres, eux, gardent leurs « %C3%A9 ».
    if ((brut.split('?', 1)[0] ?? brut).includes('%'))
        return { ok: false, raison: RAISON_PAS_GESTION };
    // L'aller-retour par l'analyseur d'URL du navigateur : même origine, et
    // chemin + paramètres identiques au caractère près.
    let u;
    try {
        u = new URL(brut, ORIGINE_TEST);
    }
    catch {
        return { ok: false, raison: RAISON_PAS_GESTION };
    }
    if (u.origin !== ORIGINE_TEST || u.pathname + u.search !== brut)
        return { ok: false, raison: RAISON_PAS_GESTION };
    const minuscule = brut.toLowerCase();
    const chemin = minuscule.split('?', 1)[0] ?? minuscule;
    if (CHEMINS_REFUSES.includes(chemin))
        return { ok: false, raison: RAISON_PAGE_REFUSEE };
    if (PREFIXES_REFUSES.some((p) => minuscule === p || minuscule.startsWith(p)))
        return { ok: false, raison: RAISON_PAGE_REFUSEE };
    if (PREFIXES_INCIDENT.some((p) => minuscule.startsWith(p)))
        return { ok: false, raison: RAISON_INCIDENT };
    return { ok: true, chemin: brut };
}
/** Caractères invisibles ou de mise en page refusés dans un nom :
 *  contrôles (Cc), formats (Cf : U+202E, zéro-largeur…), surrogats isolés
 *  (Cs), séparateurs de ligne et de paragraphe (Zl, Zp). */
const INVISIBLES = /[\p{Cc}\p{Cf}\p{Cs}\p{Zl}\p{Zp}]/u;
/**
 * SERVEUR — vérifie un nom de favori : NFC et rognage, puis 1 à 80 points de
 * code sans caractère invisible. Rend le nom rogné, jamais tronqué.
 */
export function validerLibelleFavori(brut) {
    if (typeof brut !== 'string')
        return { ok: false, raison: RAISON_LIBELLE };
    const s = brut.normalize('NFC').trim();
    const n = [...s].length;
    if (n < 1 || n > FAVORI_LIBELLE_MAX || INVISIBLES.test(s))
        return { ok: false, raison: RAISON_LIBELLE };
    return { ok: true, libelle: s };
}
/**
 * NAVIGATEUR — prépare un nom avant l'envoi : NFC, sauts de ligne et
 * contrôles changés en espace, caractères de format retirés, espaces
 * repliés, rognage, puis coupe à 80 points de code (79 + « … »). Peut rendre
 * une chaîne vide : à l'appelant de choisir un autre nom.
 */
export function normaliserLibelleFavori(brut) {
    let s = brut.normalize('NFC')
        .replace(/[\p{Cc}\p{Zl}\p{Zp}]/gu, ' ')
        .replace(/[\p{Cf}\p{Cs}]/gu, '')
        .replace(/\s+/gu, ' ')
        .trim();
    const points = [...s];
    if (points.length > FAVORI_LIBELLE_MAX)
        s = `${points.slice(0, FAVORI_LIBELLE_MAX - 1).join('').trimEnd()}…`;
    return s.normalize('NFC');
}
//# sourceMappingURL=favoris.js.map